import { Injectable, OnDestroy } from '@angular/core';

import { DashboardApiProviderService } from '@core/api';
import { select, Store } from '@ngrx/store';
import { TAllUsers } from '@project/view-models';
import { Observable, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { CustomFieldsService } from 'src/app/project/modules/custom-fields/custom-fields.service';
import { DEFAULT_DATE_FORMAT } from 'src/app/project/modules/preferences/default-date-format';
import { TPreferences } from 'src/app/project/modules/preferences/preferences.model';
import { EStore } from '../../../../shared/enums/store.enum';
import {
  checkActivityUser,
  mergeLocationActivities,
  mergeRemovedDocument,
} from '../../../activities/activities-merge';
import { logErrorInSentry } from '../../../errors/response-error';
import { ResponseErrorService } from '../../../errors/response-error.service';
import { ActivityFormatterService } from '../../../points/point-modal/point-timeline/activity-formatter.service';
import { PointActivityMergeService } from '../../../points/point-modal/point-timeline/point-activity-merge.service';
import { DashboardService } from '../../dashboard-service/dashboard.service';
import { TDashletActivityRequestData } from '../../dashboard.consts';
import { TDashletActivity } from './dashlet-activity/dashlet-activity.model';

@Injectable({
  providedIn: 'root',
})
export class DashletActivitiesService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private preferences$: Observable<TPreferences>;
  private format = DEFAULT_DATE_FORMAT;
  dashletActivities: TDashletActivity[] = null;

  constructor(
    private store: Store<{
      preferences: TPreferences;
    }>,
    private responseErrorService: ResponseErrorService,
    private customFieldsService: CustomFieldsService,
    private dashboardApiProviderService: DashboardApiProviderService,
    private dashboardService: DashboardService,
    private activityFormatterService: ActivityFormatterService,
    private pointActivityMergeService: PointActivityMergeService,
  ) {
    this.preferences$ = this.store.pipe(select(EStore.PREFERENCES));

    this.preferences$.pipe(takeUntil(this.destroy$)).subscribe((preferences) => {
      this.format = preferences?.dateFormat ? preferences.dateFormat : DEFAULT_DATE_FORMAT;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  fetchActivities({
    from = 0,
    size = 20,
    accountId = null,
    workspaceIds = [],
  }): Observable<TDashletActivity[]> {
    const body: TDashletActivityRequestData = {
      ...(accountId ? { accountId } : {}),
      ...(workspaceIds.length ? { workspaceIds } : {}),
      from,
      size,
      comments: true,
    };

    return this.dashboardApiProviderService
      .fetchActivities(body)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to fetch dashlet activities'),
        ),
      );
  }

  isSubset(array1: unknown[], array2: unknown[]): boolean {
    return array1.every((value) => array2.includes(value));
  }

  formatActivities(activities: TDashletActivity[], allUsers: TAllUsers): any {
    let formattedActivities = activities.map((activity) => {
      const formattedActivity =
        this.activityFormatterService.getFormattedDatetimeActivity(activity);
      this.activityFormatterService.compareDescriptions(formattedActivity);
      this.activityFormatterService.formatRichText(formattedActivity);
      this.activityFormatterService.formatFiles(formattedActivity);
      this.activityFormatterService.formatTags(formattedActivity);

      return formattedActivity;
    });

    this.dashboardService.setActivities(formattedActivities);

    try {
      // That try catch is important, lets us know if anything went wrong
      formattedActivities = this.pointActivityMergeService.mergeActivities(formattedActivities);

      formattedActivities.forEach((activity) => {
        mergeLocationActivities(activity);
        mergeRemovedDocument(activity);
        checkActivityUser(activity, allUsers);
      });

      return formattedActivities;
    } catch (error) {
      logErrorInSentry({ error, message: 'Activity formatting failed' });
      this.responseErrorService.errors(error);

      console.error(error);
    }
  }

  setDashletActivities(activities: TDashletActivity[]): void {
    this.dashletActivities = activities;
  }

  getDashletActivities(): TDashletActivity[] {
    return this.dashletActivities;
  }
}
